import React from 'react';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiMail,FiPhone,FiMapPin,FiShield,FiExternalLink,FiClock}=FiIcons;

const Footer=()=> {
  const contactInfo=[
    {icon: FiMail,label: "E-Mail",value: "abuse@wasacon.net",link: "mailto:abuse@wasacon.net"},
    {icon: FiPhone,label: "Telefon",value: "+49 2452 8608373",link: "tel:+4924528608373"},
    {icon: FiMapPin,label: "Adresse",value: "Ostpromenade 89, 52525 Heinsberg",link: null}
  ];

  const quickLinks=[
    {name: "Datenschutz",href: "https://www.wasacon.net/datenschutz"},
    {name: "Impressum",href: "https://www.wasacon.net/impressum"}
  ];

  return (
    <footer className="relative bg-slate-950/80 backdrop-blur-md border-t border-blue-500/20">
      <div className="container mx-auto px-6 py-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <motion.div 
            initial={{opacity: 0,y: 30}}
            whileInView={{opacity: 1,y: 0}}
            viewport={{once: true}}
            transition={{duration: 0.6}}
            className="space-y-4"
          >
            <div className="flex items-center space-x-3">
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-2 rounded-lg">
                <SafeIcon icon={FiShield} className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white">Visio Wasacon GmbH</h3>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed">
              Ihr Partner für IT-Dienstleistungen und Netzwerksicherheit in Heinsberg.
              Wir sorgen für eine sichere digitale Infrastruktur.
            </p>
          </motion.div>

          {/* Contact Info */}
          <motion.div 
            initial={{opacity: 0,y: 30}}
            whileInView={{opacity: 1,y: 0}}
            viewport={{once: true}}
            transition={{duration: 0.6,delay: 0.1}}
            className="space-y-4"
          >
            <h4 className="text-lg font-semibold text-white flex items-center space-x-2">
              <SafeIcon icon={FiPhone} className="w-5 h-5 text-blue-400" />
              <span>Kontakt</span>
            </h4>
            <div className="space-y-3">
              {contactInfo.map((item,index)=> (
                <motion.div 
                  key={index}
                  whileHover={{x: 5}}
                  className="flex items-center space-x-3 text-gray-400"
                >
                  <SafeIcon icon={item.icon} className="w-4 h-4 text-blue-400" />
                  {item.link ? (
                    <a href={item.link} className="text-sm hover:text-blue-400 transition-colors">
                      {item.value}
                    </a>
                  ) : (
                    <span className="text-sm">{item.value}</span>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div 
            initial={{opacity: 0,y: 30}}
            whileInView={{opacity: 1,y: 0}}
            viewport={{once: true}}
            transition={{duration: 0.6,delay: 0.2}}
            className="space-y-4"
          >
            <h4 className="text-lg font-semibold text-white flex items-center space-x-2">
              <SafeIcon icon={FiExternalLink} className="w-5 h-5 text-indigo-400" />
              <span>Links</span>
            </h4>
            <div className="space-y-3">
              {quickLinks.map((link,index)=> (
                <motion.a 
                  key={index}
                  href={link.href}
                  whileHover={{x: 5}}
                  className="block text-gray-400 text-sm hover:text-indigo-400 transition-colors"
                  target={link.href.startsWith('http') ? '_blank' : '_self'}
                  rel={link.href.startsWith('http') ? 'noopener noreferrer' : ''}
                >
                  {link.name}
                </motion.a>
              ))}
            </div>
          </motion.div>

          {/* Response Times */}
          <motion.div 
            initial={{opacity: 0,y: 30}}
            whileInView={{opacity: 1,y: 0}}
            viewport={{once: true}}
            transition={{duration: 0.6,delay: 0.3}}
            className="space-y-4"
          >
            <h4 className="text-lg font-semibold text-white flex items-center space-x-2">
              <SafeIcon icon={FiClock} className="w-5 h-5 text-emerald-400" />
              <span>Reaktionszeiten</span>
            </h4>
            <div className="space-y-3 text-sm text-gray-400">
              <div className="flex justify-between items-center">
                <span>Kritisch:</span>
                <span className="text-red-400 font-semibold">&lt;1 Stunde</span>
              </div>
              <div className="flex justify-between items-center">
                <span>Hoch:</span>
                <span className="text-orange-400 font-semibold">&lt;4 Stunden</span>
              </div>
              <div className="flex justify-between items-center">
                <span>Normal:</span>
                <span className="text-emerald-400 font-semibold">&lt;24 Stunden</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <motion.div 
          initial={{opacity: 0}}
          whileInView={{opacity: 1}}
          viewport={{once: true}}
          transition={{duration: 0.6,delay: 0.4}}
          className="border-t border-blue-500/10 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0"
        >
          <p className="text-gray-500 text-sm">
            © 2024 Visio Wasacon GmbH. Alle Rechte vorbehalten.
          </p>
          <p className="text-gray-500 text-sm">
            IT-Services & Security in Heinsberg
          </p>
        </motion.div>
      </div>

      {/* Animated background elements */}
      <motion.div 
        className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-blue-600 via-indigo-600 to-emerald-500"
        initial={{scaleX: 0}}
        whileInView={{scaleX: 1}}
        viewport={{once: true}}
        transition={{duration: 2,ease: "easeInOut"}}
      />
    </footer>
  );
};

export default Footer;